import { useMemo } from 'react'
import { ArrowUp, ArrowDown, Minus, Target, Building2, Repeat } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import {
  identifyBattlegrounds,
  generateNarrative,
} from '@/lib/electionNarrative'
import type {
  ElectionResults,
  ElectionResultWithDetails,
  NationalElectionSummary,
  PartyResultComparison,
  GovernmentWithDetails,
} from '@/types'
import type { BattlegroundDistrict } from '@/lib/electionNarrative'

interface PostElectionSummaryProps {
  electionResults: ElectionResults
  summary: NationalElectionSummary
  results: ElectionResultWithDetails[]
  previousResults: ElectionResultWithDetails[] | undefined
  comparison: PartyResultComparison[] | undefined
  hasPreviousElection: boolean
  government: GovernmentWithDetails | null | undefined
  governmentLoading: boolean
  thresholdPercent: number
  nationName: string
  className?: string
}

export function PostElectionSummary({
  electionResults,
  summary,
  results,
  previousResults,
  comparison,
  hasPreviousElection,
  government,
  governmentLoading,
  thresholdPercent,
  nationName,
  className,
}: PostElectionSummaryProps) {
  const battlegrounds = useMemo(
    () => identifyBattlegrounds(results, previousResults, 5, 5),
    [results, previousResults],
  )

  const narrative = useMemo(
    () =>
      generateNarrative(
        electionResults,
        summary,
        comparison,
        battlegrounds,
        thresholdPercent,
        hasPreviousElection,
        nationName,
      ),
    [electionResults, summary, comparison, battlegrounds, thresholdPercent, hasPreviousElection, nationName],
  )

  const winnerParty = summary.party_results.find(
    (p) => p.party_id === electionResults.winner_party_id,
  )

  return (
    <Card className={cn(className)}>
      <CardHeader>
        <CardTitle className="text-base">Election Summary</CardTitle>
        <CardDescription>Key results and analysis</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <NarrativeSection
          sentences={narrative.sentences}
          accentColor={winnerParty?.party_color}
        />
        <SeatChangesSection
          comparison={comparison}
          hasPreviousElection={hasPreviousElection}
          summary={summary}
        />
        <VoteSwingsSection
          comparison={comparison}
          hasPreviousElection={hasPreviousElection}
        />
        <BattlegroundsSection
          battlegrounds={battlegrounds}
          hasPreviousElection={hasPreviousElection}
        />
        <GovernmentFormationSection
          government={government}
          isLoading={governmentLoading}
          electionResults={electionResults}
        />
      </CardContent>
    </Card>
  )
}

// ============================================================================
// Narrative Section
// ============================================================================

function NarrativeSection({
  sentences,
  accentColor,
}: {
  sentences: string[]
  accentColor?: string
}) {
  if (sentences.length === 0) return null

  return (
    <div
      className="rounded-r-md border-l-4 py-3 pr-3 pl-4"
      style={{ borderLeftColor: accentColor ?? '#6b7280' }}
    >
      <p className="text-muted-foreground text-sm leading-relaxed">
        {sentences.join(' ')}
      </p>
    </div>
  )
}

// ============================================================================
// Seat Changes Section
// ============================================================================

function SeatChangesSection({
  comparison,
  hasPreviousElection,
  summary,
}: {
  comparison: PartyResultComparison[] | undefined
  hasPreviousElection: boolean
  summary: NationalElectionSummary
}) {
  // Use comparison data when available, otherwise fall back to summary
  const parties = useMemo(() => {
    if (hasPreviousElection && comparison && comparison.length > 0) {
      return [...comparison].sort((a, b) => b.current_seats - a.current_seats)
    }
    return null
  }, [comparison, hasPreviousElection])

  const summaryParties = useMemo(() => {
    if (!hasPreviousElection) {
      return [...summary.party_results]
        .filter((p) => p.seats_won > 0 || p.total_votes > 0)
        .sort((a, b) => b.seats_won - a.seats_won)
    }
    return null
  }, [summary, hasPreviousElection])

  return (
    <div>
      <h4 className="mb-2 text-sm font-medium">Seat Changes</h4>
      {!hasPreviousElection && (
        <p className="text-muted-foreground mb-2 text-xs">First election — showing results only</p>
      )}
      <div className="space-y-1.5">
        {parties
          ? parties.map((p) => (
              <div key={p.party_id} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: p.party_color }}
                />
                <span className="min-w-0 flex-1 truncate text-sm">{p.party_name}</span>
                <span className="tabular-nums text-sm font-medium">
                  {p.current_seats} seat{p.current_seats !== 1 ? 's' : ''}
                </span>
                <ChangeIndicator value={p.seat_change} suffix="" />
              </div>
            ))
          : summaryParties?.map((p) => (
              <div key={p.party_id} className="flex items-center gap-2">
                <div
                  className="h-3 w-3 shrink-0 rounded-sm"
                  style={{ backgroundColor: p.party_color }}
                />
                <span className="min-w-0 flex-1 truncate text-sm">{p.party_name}</span>
                <span className="tabular-nums text-sm font-medium">
                  {p.seats_won} seat{p.seats_won !== 1 ? 's' : ''}
                </span>
              </div>
            ))}
      </div>
    </div>
  )
}

// ============================================================================
// Vote Swings Section
// ============================================================================

function VoteSwingsSection({
  comparison,
  hasPreviousElection,
}: {
  comparison: PartyResultComparison[] | undefined
  hasPreviousElection: boolean
}) {
  if (!hasPreviousElection || !comparison || comparison.length === 0) {
    return null
  }

  // Sort by current vote share descending
  const sorted = [...comparison].sort((a, b) => b.current_share - a.current_share)

  // Find biggest winner and biggest loser by vote change
  const biggestWinnerId = sorted.reduce((best, p) =>
    p.vote_change > (best?.vote_change ?? -Infinity) ? p : best,
    sorted[0],
  ).party_id
  const biggestLoserId = sorted.reduce((worst, p) =>
    p.vote_change < (worst?.vote_change ?? Infinity) ? p : worst,
    sorted[0],
  ).party_id

  return (
    <div>
      <h4 className="mb-2 text-sm font-medium">Vote Share Swings</h4>
      <div className="space-y-1.5">
        {sorted.map((p) => {
          const isBiggestWinner = p.party_id === biggestWinnerId && p.vote_change > 0
          const isBiggestLoser = p.party_id === biggestLoserId && p.vote_change < 0

          return (
            <div
              key={p.party_id}
              className={cn(
                'flex items-center gap-2 rounded px-2 py-1',
                isBiggestWinner && 'bg-green-50 dark:bg-green-950/20',
                isBiggestLoser && 'bg-red-50 dark:bg-red-950/20',
              )}
            >
              <div
                className="h-3 w-3 shrink-0 rounded-sm"
                style={{ backgroundColor: p.party_color }}
              />
              <span className="min-w-0 flex-1 truncate text-sm">
                {p.party_name}
                {isBiggestWinner && (
                  <Badge variant="outline" className="ml-1.5 px-1 py-0 text-xs text-green-600">
                    Biggest winner
                  </Badge>
                )}
                {isBiggestLoser && (
                  <Badge variant="outline" className="ml-1.5 px-1 py-0 text-xs text-red-600">
                    Biggest loser
                  </Badge>
                )}
              </span>
              <span className="tabular-nums text-sm">{p.current_share.toFixed(1)}%</span>
              <ChangeIndicator value={p.vote_change} suffix="%" decimals={1} />
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// Key Battlegrounds Section
// ============================================================================

function BattlegroundsSection({
  battlegrounds,
  hasPreviousElection,
}: {
  battlegrounds: BattlegroundDistrict[]
  hasPreviousElection: boolean
}) {
  if (battlegrounds.length === 0) {
    return (
      <div>
        <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium">
          <Target className="h-3.5 w-3.5" />
          Key Battlegrounds
        </h4>
        <p className="text-muted-foreground text-sm">No close contests in this election.</p>
      </div>
    )
  }

  return (
    <div>
      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium">
        <Target className="h-3.5 w-3.5" />
        Key Battlegrounds
      </h4>
      <div className="space-y-2">
        {battlegrounds.map((bg) => (
          <div
            key={bg.districtId}
            className="bg-muted/30 flex items-start justify-between rounded px-3 py-2"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium">{bg.districtName}</span>
                {bg.regionName && (
                  <span className="text-muted-foreground text-xs">({bg.regionName})</span>
                )}
                {bg.flipped && hasPreviousElection && (
                  <Badge variant="outline" className="px-1 py-0 text-xs text-amber-600">
                    <Repeat className="mr-0.5 h-2.5 w-2.5" />
                    Flipped
                  </Badge>
                )}
              </div>
              <div className="mt-1 flex items-center gap-3 text-xs">
                <span className="flex items-center gap-1">
                  <div
                    className="h-2 w-2 rounded-sm"
                    style={{ backgroundColor: bg.winnerPartyColor }}
                  />
                  {bg.winnerPartyName} {bg.winnerVoteShare.toFixed(1)}%
                </span>
                <span className="text-muted-foreground">vs</span>
                <span className="flex items-center gap-1">
                  <div
                    className="h-2 w-2 rounded-sm"
                    style={{ backgroundColor: bg.runnerUpPartyColor }}
                  />
                  {bg.runnerUpPartyName} {bg.runnerUpVoteShare.toFixed(1)}%
                </span>
              </div>
            </div>
            <span className="tabular-nums text-sm font-medium text-amber-600">
              {bg.margin.toFixed(1)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Government Formation Section
// ============================================================================

function GovernmentFormationSection({
  government,
  isLoading,
  electionResults,
}: {
  government: GovernmentWithDetails | null | undefined
  isLoading: boolean
  electionResults: ElectionResults
}) {
  return (
    <div>
      <h4 className="mb-2 flex items-center gap-1.5 text-sm font-medium">
        <Building2 className="h-3.5 w-3.5" />
        Government Formation
      </h4>

      {isLoading && (
        <div className="bg-muted h-5 w-64 animate-pulse rounded" />
      )}

      {!isLoading && !government && (
        <p className="text-muted-foreground text-sm">
          {electionResults.majority_achieved
            ? 'Government formation pending...'
            : 'No party has achieved a majority. Coalition negotiations are underway.'}
        </p>
      )}

      {!isLoading && government && (
        <div className="bg-muted/30 flex items-center gap-2 rounded px-3 py-2">
          {government.lead_party && (
            <div
              className="h-3 w-3 shrink-0 rounded-sm"
              style={{ backgroundColor: government.lead_party.color }}
            />
          )}
          <p className="text-sm">
            {government.status === 'forming' && (
              <>
                The {government.lead_party?.name ?? 'leading party'} is forming a{' '}
                {government.government_type} government.
              </>
            )}
            {government.status === 'active' && government.government_type === 'majority' && (
              <>
                The {government.lead_party?.name ?? 'leading party'} has formed a majority
                government with {government.seats_held} of {government.total_seats} seats.
              </>
            )}
            {government.status === 'active' && government.government_type === 'coalition' && (
              <>
                A {government.lead_party?.name ?? 'leading party'}-led coalition government has
                been formed with {government.seats_held} of {government.total_seats} seats.
              </>
            )}
            {government.status === 'active' && government.government_type === 'minority' && (
              <>
                The {government.lead_party?.name ?? 'leading party'} is governing as a minority
                with {government.seats_held} of {government.total_seats} seats.
              </>
            )}
            {government.status === 'caretaker' && (
              <>A caretaker government is in place pending new elections.</>
            )}
          </p>
          <Badge variant="outline" className="ml-auto shrink-0 text-xs">
            {government.government_type}
          </Badge>
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Shared Change Indicator
// ============================================================================

function ChangeIndicator({
  value,
  suffix,
  decimals = 0,
}: {
  value: number
  suffix: string
  decimals?: number
}) {
  if (Math.abs(value) < 0.05) {
    return (
      <span className="text-muted-foreground inline-flex items-center gap-0.5 text-sm">
        <Minus className="h-3 w-3" />
        0{suffix}
      </span>
    )
  }

  const formatted = decimals > 0 ? Math.abs(value).toFixed(decimals) : Math.abs(value)

  if (value > 0) {
    return (
      <span className="tabular-nums inline-flex items-center gap-0.5 text-sm text-green-600">
        <ArrowUp className="h-3 w-3" />
        +{formatted}{suffix}
      </span>
    )
  }

  return (
    <span className="tabular-nums inline-flex items-center gap-0.5 text-sm text-red-600">
      <ArrowDown className="h-3 w-3" />
      -{formatted}{suffix}
    </span>
  )
}
